import { forwardRef } from "react";
import {
  motion as fmMotion,
  AnimatePresence,
  useInView,
  useAnimation,
  useMotionValue,
  useSpring,
  useTransform,
} from "framer-motion";
import { useAppMotionContext } from "../context/AppMotionContext";

const MOTION_TAGS = [
  "div",
  "section",
  "article",
  "main",
  "header",
  "footer",
  "nav",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "p",
  "span",
  "img",
  "button",
  "a",
  "form",
  "svg",
  "label",
  "input",
];

function createAppMotionComponent(tag) {
  const FmComponent = fmMotion[tag];
  if (!FmComponent) {
    throw new Error(
      `appMotion: "${tag}" is not a valid framer-motion element. Import from "framer-motion" directly or add it to MOTION_TAGS.`,
    );
  }

  return forwardRef(function AppMotionComponent(
    {
      variant,
      delay,
      duration,
      initial,
      whileInView,
      viewport,
      transition,
      variants,
      animate,
      exit,
      ...rest
    },
    ref,
  ) {
    const { getProps, getInViewVariants, isStatic } = useAppMotionContext();

    if (!isStatic) {
      return (
        <FmComponent
          ref={ref}
          initial={initial}
          whileInView={whileInView}
          viewport={viewport}
          transition={transition}
          variants={variants}
          animate={animate}
          exit={exit}
          {...rest}
        />
      );
    }

    if (variants != null && whileInView != null) {
      return <FmComponent ref={ref} {...getInViewVariants(variants)} {...rest} />;
    }

    if (whileInView != null) {
      const resolvedDuration = transition?.duration ?? duration ?? 0.5;
      const resolvedDelay = transition?.delay ?? delay ?? 0;
      return (
        <FmComponent
          ref={ref}
          {...getProps({
            variant,
            delay: resolvedDelay,
            duration: resolvedDuration,
          })}
          {...rest}
        />
      );
    }

    return (
      <FmComponent
        ref={ref}
        initial={initial}
        animate={animate}
        exit={exit}
        transition={transition}
        variants={variants}
        {...rest}
      />
    );
  });
}

const motionCache = Object.create(null);

const motion = new Proxy(motionCache, {
  get(_target, prop) {
    if (typeof prop !== "string") return undefined;
    if (!motionCache[prop] && fmMotion[prop]) {
      motionCache[prop] = createAppMotionComponent(prop);
    }
    return motionCache[prop];
  },
});

for (const tag of MOTION_TAGS) {
  motion[tag];
}

export {
  motion,
  AnimatePresence,
  useInView,
  useAnimation,
  useMotionValue,
  useSpring,
  useTransform,
};
